import { PropsWithChildren, useEffect, useMemo } from 'react';
import Modal, {
  ModalHeader,
  ModalContent,
  ModalFooter
} from 'components/ui/Modal';
import MoreOptionsTabs, { TabId } from '.';
import ActivityChoicesTab from './activity-choices-tab';
import LogDataTab from './log-data-tab';
import AllowedAppsTab from './allowed-apps-tab';
import VideosTab from './videos-tab';
import AdvancedTab from './advanced-tab';
import TimingTab from './timing-tab';
import InstructionsTab from './instructions-tab';
import { t } from 'services/i18n.service';
import BaseButton from 'components/ui/BaseButton';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import {
  updateMoreOptionActiveTab,
  updateMoreOptionRefreshDuration
} from 'store/reducer/modal/slice';
import {
  updateActivityFromMoreOptions,
  updateIsShowMoreOptionsOpened
} from 'store/reducer/setting/slice';
import {
  modalContentSelector,
  moreOptionModalActionsSelector
} from 'store/reducer/modal/more-options.modal.selectors';
import { normalizeActivityLogQuantityQuestions } from 'utils/log-quantity-questions.util';
import { ACTIVITY_TYPES, OVERLAY_Z_INDEX_CLASS } from 'constants/general';
import { findCustomRoutinePosition } from 'utils/findCustomRoutinePosition';
import {
  ActivityInstructionImagesDraftProvider,
  useActivityInstructionImagesDraftContext
} from 'contexts/ActivityInstructionImagesDraftContext';
import {
  getMoreOptionsModalActionState,
  getMoreOptionsVisibleTabIds
} from 'utils/more-options.util';
import Badge from 'components/ui/Badge';
import { MORE_OPTION_REVAMP_TAB, PackEditorTabMode } from 'enums';
import Tags from './Tags';

const ModalContentWrapper = ({ children }: PropsWithChildren) => (
  <div className='w-full h-fit flex flex-col ~gap-10/6 ~px-2/4 ~py-1.5/4'>
    {children}
  </div>
);

const TABS_CONTENTS: Record<MORE_OPTION_REVAMP_TAB, React.ReactNode> = {
  [MORE_OPTION_REVAMP_TAB.ACTIVITY_CHOICES]: <ActivityChoicesTab />,
  [MORE_OPTION_REVAMP_TAB.TIMING]: <TimingTab />,
  [MORE_OPTION_REVAMP_TAB.VIDEOS]: <VideosTab />,
  [MORE_OPTION_REVAMP_TAB.ALLOWED_APPS]: <AllowedAppsTab />,
  [MORE_OPTION_REVAMP_TAB.LOG_DATA]: <LogDataTab />,
  [MORE_OPTION_REVAMP_TAB.INSTRUCTIONS]: <InstructionsTab />,
  [MORE_OPTION_REVAMP_TAB.ADVANCED]: <AdvancedTab />
};

const MoreOptionsModalRevampInner: React.FC = () => {
  const dispatch = useAppDispatch();
  const { commitDraft, discardDraft } =
    useActivityInstructionImagesDraftContext();
  const {
    moreOption: { activity, activeTab },
    moreOptions: { type, position },
    currentSettings,
    mode,
    activityLibrary,
    packEditorTabMode
  } = useAppSelectorShallow(moreOptionModalActionsSelector);
  const { activityType, platform, flags } =
    useAppSelectorShallow(modalContentSelector);

  const visibleTabIds = useMemo(
    () =>
      getMoreOptionsVisibleTabIds({
        context: { activityType, platform, flags }
      }),
    [activityType, flags, platform]
  );

  useEffect(() => {
    if (!visibleTabIds.includes(activeTab)) {
      dispatch(
        updateMoreOptionActiveTab(
          visibleTabIds[0] ?? MORE_OPTION_REVAMP_TAB.TIMING
        )
      );
    }
  }, [activeTab, dispatch, visibleTabIds]);

  const customRoutinePosition =
    type === ACTIVITY_TYPES.CUSTOM_ROUTINES
      ? findCustomRoutinePosition(activity)
      : undefined;

  const { isDirty, isValid, canSave, isDaysOfWeekValid } = useMemo(
    () =>
      getMoreOptionsModalActionState({
        activity,
        type,
        position,
        currentSettings,
        mode,
        activityLibrary,
        customRoutinePosition
      }),
    [
      activity,
      type,
      position,
      currentSettings,
      mode,
      activityLibrary,
      customRoutinePosition
    ]
  );

  const handleTabChange = (tabId: TabId) => {
    dispatch(updateMoreOptionActiveTab(tabId));
  };

  const handleClose = () => {
    discardDraft(dispatch);
    dispatch(updateIsShowMoreOptionsOpened(false));
  };

  const handleSave = async () => {
    if (!canSave) {
      return;
    }
    await commitDraft(dispatch);
    const committedActivity = normalizeActivityLogQuantityQuestions(activity);
    dispatch(
      updateActivityFromMoreOptions({
        type,
        position,
        value: committedActivity,
        customRoutinePosition
      })
    );
    dispatch(updateIsShowMoreOptionsOpened(false));
    dispatch(updateMoreOptionRefreshDuration(true));
  };

  return (
    <Modal
      isOpen
      onClose={handleClose}
      size='large'
      variant='withTabs'
      zIndex={OVERLAY_Z_INDEX_CLASS.MODAL_OVER_DRAWER}>
      <ModalHeader
        title={activity?.name || t('tabs_revamp.more_options.more_options')}
        onClose={handleClose}
      />
      <MoreOptionsTabs activeTab={activeTab} onTabChange={handleTabChange} />
      <ModalContent className='bg-white dark:bg-secondary-dark-300 min-h-[45vh] ~px-2/4'>
        <ModalContentWrapper>
          {TABS_CONTENTS[activeTab]}
          {packEditorTabMode === PackEditorTabMode.ROUTINE && <Tags />}
        </ModalContentWrapper>
      </ModalContent>
      <ModalFooter className='bg-white dark:bg-secondary-dark-200 flex flex-col ~gap-2/3 items-center'>
        {isDirty && !isDaysOfWeekValid && (
          <Badge
            variant='destructive'
            size='xs'
            className='self-center !w-10/12 text-wrap'>
            {t('validation.select_at_least_one_day_for_habit')}
          </Badge>
        )}
        {isDirty && (
          <BaseButton
            title={t('save')}
            onClick={handleSave}
            disabled={!isValid}
            className='~px-4/8 mx-auto'
            size='sm'
          />
        )}
      </ModalFooter>
    </Modal>
  );
};

const MoreOptionsModalRevamp: React.FC = () => {
  const isOpen = useAppSelectorShallow(
    (state) => state.setting.isShowMoreOptionsOpened
  );

  if (!isOpen) {
    return null;
  }

  return (
    <ActivityInstructionImagesDraftProvider>
      <MoreOptionsModalRevampInner />
    </ActivityInstructionImagesDraftProvider>
  );
};

export default MoreOptionsModalRevamp;
